import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';

import { FormErrorBase } from '@dashboard/components/Formik/components/FormError';

export { PasswordInput, renderAuthField } from './PasswordInput';
export {
  PASSWORD_TOO_SHORT,
  authButtonClassName,
  authInputClassName,
  authLink,
  newPasswordField,
  newPasswordSchema,
  passwordHint,
} from './authRules';
export { AuthErrorLink, AuthLink } from './AuthLink';
export { authRedirectPath, newAccountPath } from './authUrl';

export function AuthHeader({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mb-7">
      <h1 className="text-balance text-2xl font-semibold tracking-tight text-gray-900 dark:text-foreground sm:text-[28px] sm:leading-tight">
        {title}
      </h1>
      {description && (
        <p className="mt-2 text-[15px] leading-relaxed text-gray-600 dark:text-muted-foreground">
          {description}
        </p>
      )}
    </div>
  );
}

export function AuthFooter({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-7 border-t border-gray-100 dark:border-border pt-5 text-center text-sm text-gray-600 dark:text-muted-foreground">
      {children}
    </p>
  );
}

export function AuthTerms() {
  return (
    <p className="mt-5 text-balance text-center text-xs leading-relaxed text-gray-500 dark:text-muted-foreground">
      By creating an account, you agree to the{' '}
      <a
        href="/terms-of-service"
        target="_blank"
        className="whitespace-nowrap underline underline-offset-2"
      >
        Terms of Service
      </a>{' '}
      and{' '}
      <a
        href="/privacy-policy"
        target="_blank"
        className="whitespace-nowrap underline underline-offset-2"
      >
        Privacy policy
      </a>
      .
    </p>
  );
}

export function AuthDivider() {
  return (
    <div className="my-6 flex items-center gap-3 text-sm text-gray-500 dark:text-muted-foreground">
      <span className="h-px flex-1 bg-gray-200 dark:bg-accent" />
      or
      <span className="h-px flex-1 bg-gray-200 dark:bg-accent" />
    </div>
  );
}

export function WatchValues<T>({ onChange }: { onChange: (values: T) => void }) {
  const { values } = useFormikContext<T>();

  useEffect(() => onChange(values), [values, onChange]);

  return null;
}

export function AuthFieldError({ children }: { children: React.ReactNode }) {
  return (
    <div role="alert">
      <FormErrorBase>{children}</FormErrorBase>
    </div>
  );
}

// The form engine skips re-rendering a field unless its value, error or a few
// known props change, so a server error shown on a field has to be compared here.
export const invalidFieldPropsAreEqual = (
  prev: { field: Record<string, unknown> },
  next: { field: Record<string, unknown> },
) => (prev.field['aria-invalid'] === next.field['aria-invalid'] ? undefined : false);
